/**
 * @file ModalShell.ts
 * @role Shared Lucky Lights modal chrome: backdrop, luxe safe-area card, header, close action,
 *       content/footer roots, and finite entrance presentation.
 * @owns Modal chrome, safe-frame card geometry, close/backdrop input, and animation reset.
 * @reads LayoutContext, validated UI tokens, and injected close-button textures.
 * @neverDoes Decide modal contents or game flow. Read window. Load assets.
 * @invariants 8, 12, 14.
 */
import { Container, Graphics, Text, type Texture } from 'pixi.js';
import { gsap } from 'gsap';
import type { LayoutContext } from '@core/layout/LayoutContext';
import type { ModalLayoutConfig, UiThemeConfig } from '@core/config/UiConfig';
import { IconButton } from './IconButton';

export type ModalPlacement = 'center' | 'bottom';

export interface ModalShellTextures {
  close?: Texture | undefined;
  roundButtonBackground?: Texture | undefined;
}

export interface ModalShellOptions {
  title: string;
  layout: ModalLayoutConfig;
  theme: UiThemeConfig;
  textures?: ModalShellTextures;
  dismissible?: boolean;
  reducedMotion?: boolean;
}

export interface ModalContentBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class ModalShell extends Container {
  readonly content = new Container();
  readonly footer = new Container();
  private readonly backdrop = new Graphics();
  private readonly panelRoot = new Container();
  private readonly glow = new Graphics();
  private readonly card = new Graphics();
  private readonly divider = new Graphics();
  private readonly title: Text;
  private readonly closeButton: IconButton;
  private closeHandler: (() => void) | undefined;
  private transition: gsap.core.Animation | undefined;
  private dismissible: boolean;
  private cardBounds = { x: 0, y: 0, width: 0, height: 0 };
  private contentBounds: ModalContentBounds = { x: 0, y: 0, width: 0, height: 0 };

  constructor(private readonly options: ModalShellOptions) {
    super();
    this.dismissible = options.dismissible ?? true;
    this.visible = false;
    this.backdrop.eventMode = 'static';
    this.backdrop.cursor = this.dismissible ? 'pointer' : 'default';
    this.backdrop.on('pointertap', () => {
      if (this.dismissible) this.closeHandler?.();
    });
    this.title = new Text({
      text: options.title,
      style: {
        fill: options.theme.textPrimary,
        fontSize: options.layout.headerFontSize,
        fontWeight: '800',
        letterSpacing: 1.5,
      },
    });
    this.title.anchor.set(0, 0.5);
    this.closeButton = new IconButton({
      glyph: '×',
      diameter: options.layout.closeDiameter,
      texture: options.textures?.close,
      backgroundTexture: options.textures?.roundButtonBackground,
      accessibleTitle: `Close ${options.title.toLowerCase()}`,
      tabIndex: 1,
      iconScaleRatio: 0.46,
    });
    this.closeButton.visible = this.dismissible;
    this.closeButton.onTap(() => this.closeHandler?.());
    this.panelRoot.addChild(
      this.glow,
      this.card,
      this.divider,
      this.title,
      this.closeButton,
      this.content,
      this.footer,
    );
    this.addChild(this.backdrop, this.panelRoot);
    this.setAccessibilityEnabled(false);
  }

  onRequestClose(handler: () => void): this {
    this.closeHandler = handler;
    return this;
  }

  /** Discrete close-button activation only; backdrop dismissal is intentionally silent. */
  onActivated(handler: () => void): this {
    this.closeButton.onActivated(handler);
    return this;
  }

  setTitle(title: string): void {
    this.title.text = title;
  }

  setDismissible(dismissible: boolean): void {
    this.dismissible = dismissible;
    this.closeButton.visible = dismissible;
    this.backdrop.cursor = dismissible ? 'pointer' : 'default';
    this.closeButton.setAccessibilityEnabled(dismissible && this.visible);
  }

  show(): void {
    this.killTransition();
    this.visible = true;
    this.alpha = 1;
    this.backdrop.alpha = 1;
    this.panelRoot.alpha = 1;
    this.panelRoot.scale.set(1);
    this.setAccessibilityEnabled(true);
    if (this.options.reducedMotion || this.options.layout.enterDurationMs === 0) return;
    this.backdrop.alpha = 0;
    this.panelRoot.alpha = 0;
    this.panelRoot.scale.set(0.96);
    this.transition = gsap.to(this.backdrop, {
      alpha: 1,
      duration: this.options.layout.enterDurationMs / 1000,
      ease: 'power1.out',
    });
    gsap.to(this.panelRoot, {
      alpha: 1,
      duration: this.options.layout.enterDurationMs / 1000,
      ease: 'power2.out',
    });
    gsap.to(this.panelRoot.scale, {
      x: 1,
      y: 1,
      duration: this.options.layout.enterDurationMs / 1000,
      ease: 'back.out(1.25)',
    });
  }

  hide(animate = false, onComplete?: () => void): void {
    this.killTransition();
    this.setAccessibilityEnabled(false);
    if (
      !animate ||
      this.options.reducedMotion ||
      this.options.layout.exitDurationMs === 0 ||
      !this.visible
    ) {
      this.resetHiddenState();
      onComplete?.();
      return;
    }
    const duration = this.options.layout.exitDurationMs / 1000;
    gsap.to(this.backdrop, { alpha: 0, duration, ease: 'power1.in' });
    gsap.to(this.panelRoot, { alpha: 0, duration, ease: 'power1.in' });
    this.transition = gsap.to(this.panelRoot.scale, {
      x: 0.98,
      y: 0.98,
      duration,
      ease: 'power1.in',
      onComplete: () => {
        this.resetHiddenState();
        onComplete?.();
      },
    });
  }

  layout(
    layout: LayoutContext,
    desiredWidth: number,
    desiredHeight: number,
    placement: ModalPlacement = 'center',
    bottomInset = 0,
  ): ModalContentBounds {
    const config = this.options.layout;
    const safe = layout.safeBounds;
    const margin = Math.min(
      config.viewportMargin,
      Math.max(8, Math.min(safe.width, safe.height) * 0.04),
    );
    const width = Math.min(desiredWidth, Math.max(1, safe.width - margin * 2));
    const height = Math.min(desiredHeight, Math.max(1, safe.height - margin * 2));
    const x = safe.x + (safe.width - width) / 2;
    const y =
      placement === 'bottom'
        ? Math.max(safe.y + margin, safe.bottom - margin - bottomInset - height)
        : safe.y + (safe.height - height) / 2;
    this.cardBounds = { x, y, width, height };
    this.panelRoot.position.set(x, y);
    this.panelRoot.pivot.set(width / 2, height / 2);
    this.panelRoot.position.set(x + width / 2, y + height / 2);

    this.backdrop
      .clear()
      .rect(0, 0, layout.width, layout.height)
      .fill({ color: this.options.theme.overlay, alpha: config.backdropAlpha });
    this.glow
      .clear()
      .roundRect(-7, -7, width + 14, height + 14, config.cardRadius + 7)
      .fill({ color: this.options.theme.glow, alpha: 0.16 });
    this.card
      .clear()
      .roundRect(0, 0, width, height, config.cardRadius)
      .fill({ color: this.options.theme.surface, alpha: 0.985 })
      .roundRect(0, 0, width, height, config.cardRadius)
      .stroke({ width: config.strokeWidth, color: this.options.theme.border })
      .roundRect(3, 3, width - 6, height - 6, Math.max(0, config.cardRadius - 3))
      .stroke({ width: 1, color: this.options.theme.accent, alpha: 0.36 });
    const headerHeight = Math.min(config.headerHeight, height);
    this.divider
      .clear()
      .rect(config.cardPadding, headerHeight, Math.max(0, width - config.cardPadding * 2), 1)
      .fill({ color: this.options.theme.accent, alpha: 0.22 });
    const padding = Math.min(config.cardPadding, Math.max(16, width * 0.06));
    this.title.position.set(padding, headerHeight / 2);
    const closeRadius = config.closeDiameter / 2;
    const closeCenterInset = config.closeEdgeInset + closeRadius;
    this.closeButton.position.set(
      Math.max(closeRadius, width - closeCenterInset),
      Math.min(
        Math.max(closeRadius, closeCenterInset),
        Math.max(closeRadius, height - closeRadius),
      ),
    );
    this.contentBounds = {
      x: padding,
      y: headerHeight + Math.max(12, config.sectionGap * 0.6),
      width: Math.max(1, width - padding * 2),
      height: Math.max(1, height - headerHeight - padding - Math.max(12, config.sectionGap * 0.6)),
    };
    this.content.position.set(this.contentBounds.x, this.contentBounds.y);
    this.footer.position.set(padding, height - padding);
    return { ...this.contentBounds };
  }

  get debugCardBounds(): { x: number; y: number; width: number; height: number } {
    return { ...this.cardBounds };
  }

  get debugContentBounds(): ModalContentBounds {
    return { ...this.contentBounds };
  }

  get debugClosePoint(): { x: number; y: number } {
    return {
      x: this.cardBounds.x + this.closeButton.x,
      y: this.cardBounds.y + this.closeButton.y,
    };
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.killTransition();
    super.destroy(options);
  }

  private setAccessibilityEnabled(enabled: boolean): void {
    this.closeButton.setAccessibilityEnabled(enabled && this.dismissible);
  }

  private killTransition(): void {
    this.transition?.kill();
    this.transition = undefined;
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panelRoot);
    gsap.killTweensOf(this.panelRoot.scale);
  }

  private resetHiddenState(): void {
    this.visible = false;
    this.alpha = 1;
    this.backdrop.alpha = 1;
    this.panelRoot.alpha = 1;
    this.panelRoot.scale.set(1);
  }
}
